import { Data2, describeHelpers, Labels } from "../../../features/data-types"
import { RoutePath } from "../../../features/routes"
import { Params } from "../../../state/reducers/types"
import { DataType } from "../../../tables"
import { ListResponse } from "../../common"
import { useBranchLoaders, useManagerHooks } from "../../manager"
import { Operation, useOperationLoaders } from "../../operations"
import { useCustomerLoaders } from "../../sales"
import { useDeliveryLoaders } from "../loaders/deliveryLoaders"
import { useDeliveryOrderLoaders } from "../loaders/deliveryOrderLoaders"
import { Delivery, DeliveryMethod, DeliveryOrder, DeliveryStatus, NotDeliveredItem } from "../state/types"
import { DeliveryOrderFilters, DeliveryOrderStatusFilter } from "./types"

export const useDeliveryOrderHooks = () => {
    const { currentBranchId } = useManagerHooks()

    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const deliveryLoaders = useDeliveryLoaders()
    const customerLoaders = useCustomerLoaders()
    const branchLoaders = useBranchLoaders()
    const operationLoaders = useOperationLoaders()

    const getDeliveryAddress = async (deliveryElement: DeliveryOrder | Delivery) => {
        if ([DeliveryMethod.PICKUP, DeliveryMethod.RECEPTION, DeliveryMethod.RETURN].includes(deliveryElement.method)) {
            const { findBranch } = branchLoaders()
            const branch = findBranch(deliveryElement.branchId)!
            return branch.address
        } else {
            const { loadCustomer } = customerLoaders()
            const customer = await loadCustomer(deliveryElement.customerId!)
            return customer!.deliveryAddresses.find(address => address.id === deliveryElement.addressId)!
        }
    }

    const addNotDeliveredCount = (
        deliveryOrder: DeliveryOrder,
        newCount: NotDeliveredItem[]
    ) => {
        const existingNotDeliveredCount = deliveryOrder.notDeliveredCount ?? []
        const notDeliveredCountMap = new Map<string, number>()
        existingNotDeliveredCount.forEach(item => {
            notDeliveredCountMap.set(item.itemId, item.quantity)
        })
        newCount.forEach(item => {
            if (item.quantity > 0) {
                const prev = notDeliveredCountMap.get(item.itemId) ?? 0
                notDeliveredCountMap.set(item.itemId, prev + item.quantity)
            }
        })
        const mergedNotDeliveredCount: NotDeliveredItem[] = Array.from(notDeliveredCountMap.entries())
            .filter(([_itemId, quantity]) => quantity > 0)
            .map(([itemId, quantity]) => ({ itemId, quantity }))
        return mergedNotDeliveredCount
    }

    const removeNotDeliveredCount = (
        deliveryOrder: DeliveryOrder,
        toRemoveCount: NotDeliveredItem[] = []
    ): NotDeliveredItem[] => {
        const countMap = new Map<string, number>()
        const existingNotDeliveredCount = deliveryOrder.notDeliveredCount ?? []
        existingNotDeliveredCount.forEach(item => {
            countMap.set(item.itemId, item.quantity)
        })
        toRemoveCount.forEach(item => {
            if (countMap.has(item.itemId)) {
                const newQty = countMap.get(item.itemId)! - item.quantity
                if (newQty > 0) {
                    countMap.set(item.itemId, newQty)
                } else {
                    countMap.delete(item.itemId)
                }
            }
        })
        return Array.from(countMap.entries()).map(([itemId, quantity]) => ({ itemId, quantity }))
    }

    const loadDeliveryOrdersBatch = async (
        filtersData: Data2 = {},
        batchSize?: number,
        startKey?: Params
    ): Promise<ListResponse<DeliveryOrder>> => {
        const { searchText, method, table, customerId, deliveryStatus, creationDateFrom, creationDateTo, dateFrom, dateTo } = filtersData as DeliveryOrderFilters

        const sort = {
            index: 'DeliveryDateIndex',
            prop: 'deliveryDate',
            asc: false
        }
        const textFilters = searchText?.trim() ?
            [{ prop: 'codeStr', text: searchText.trim() }] :
            undefined

        const valueFilters: { prop: string, value: string | boolean, equals?: boolean }[] = [
            { prop: 'branchId', value: currentBranchId! },
            { prop: 'method', value: method! }
        ]
        table && valueFilters.push({ prop: 'table', value: table })
        if (deliveryStatus === DeliveryOrderStatusFilter.CANCELLED) {
            valueFilters.push({ prop: 'cancelled', value: true })
        } else if (deliveryStatus === DeliveryOrderStatusFilter.NOT_DELIVERED) {
            valueFilters.push({ prop: 'deliveryStatus', value: DeliveryStatus.DELIVERED, equals: false })
            valueFilters.push({ prop: 'cancelled', value: true, equals: false })
        } else if (deliveryStatus) {
            valueFilters.push({ prop: 'deliveryStatus', value: deliveryStatus })
            valueFilters.push({ prop: 'cancelled', value: true, equals: false })
        }
        customerId && valueFilters.push({ prop: 'customerId', value: customerId })

        const creationDateTimeFrom = creationDateFrom
        creationDateTimeFrom?.setHours(0, 0, 0, 0)
        const creationDateTimeTo = creationDateTo
        creationDateTimeTo?.setHours(23, 59, 59, 999)
        const dateTimeFrom = dateFrom
        dateTimeFrom?.setHours(0, 0, 0, 0)
        const dateTimeTo = dateTo
        dateTimeTo?.setHours(23, 59, 59, 999)

        const rangeFilters = []
        if (dateTimeFrom || dateTimeTo) {
            rangeFilters.push({
                prop: 'deliveryDate',
                ...(dateTimeFrom && { start: dateTimeFrom.toISOString() }),
                ...(dateTimeTo && { end: dateTimeTo.toISOString() })
            })
        }
        if (creationDateTimeFrom || creationDateTimeTo) {
            rangeFilters.push({
                prop: 'creationDate',
                ...(creationDateTimeFrom && { start: creationDateTimeFrom.toISOString() }),
                ...(creationDateTimeTo && { end: creationDateTimeTo.toISOString() })
            })
        }

        const response = await deliveryOrderLoaders().loadDeliveryOrdersBatch({
            startKey,
            limit: batchSize,
            sort,
            textFilters,
            valueFilters,
            rangeFilters: rangeFilters.length > 0 ? rangeFilters : undefined
        }, false)

        return response as ListResponse<DeliveryOrder>
    }

    const loadBatchData = async (deliveryOrdersDT: DataType[]) => {
        const deliveryOrders = deliveryOrdersDT as DeliveryOrder[]
        const { loadOrdersDeliveries } = deliveryLoaders()
        await loadOrdersDeliveries(deliveryOrders.map(order => order.id))
        const { loadDeliveriesOperations } = operationLoaders()
        await loadDeliveriesOperations(deliveryOrders)
        const { loadCustomers } = customerLoaders()
        const customerIds = deliveryOrders.filter(order => !!order.customerId).map(order => order.customerId!)
        await loadCustomers(customerIds)
    }

    return {
        getDeliveryAddress,
        addNotDeliveredCount,
        removeNotDeliveredCount,
        loadDeliveryOrdersBatch,
        loadBatchData
    }
}

export const deliveryOrderHelpers = {
    describeDeliveryOrder: (deliveryOrder: DeliveryOrder): string => {
        return `Orden de ${Labels.deliveryMethodLabel(deliveryOrder.method)} ${describeHelpers.describeCode(deliveryOrder)}`
    },

    getDeliveryMethodIcon: (deliveryMethod: DeliveryMethod): string => {
        return new Map<DeliveryMethod, string>()
            .set(DeliveryMethod.RECEPTION, 'cart-check')
            .set(DeliveryMethod.PICKUP, 'bag-fill')
            .set(DeliveryMethod.SHIPMENT, 'truck')
            .set(DeliveryMethod.RETURN, 'boxes')
            .get(deliveryMethod)!
    },

    getDeliveryMethodRoutPath: (deliveryMethod: DeliveryMethod): RoutePath => {
        return new Map<DeliveryMethod, RoutePath>()
            .set(DeliveryMethod.PICKUP, RoutePath.PICKUP_ORDERS)
            .set(DeliveryMethod.SHIPMENT, RoutePath.SHIPMENT_ORDERS)
            .set(DeliveryMethod.RECEPTION, RoutePath.RECEPTION_ORDERS)
            .get(deliveryMethod)!
    },

    isDeliverable: (deliveryOrder: DeliveryOrder): boolean => {
        return deliveryOrder.items.some(item => item.deliveryStats?.pending > 0) && !deliveryOrder.cancelled
    },

    isDeliveryCompletable: (deliveryOrder: DeliveryOrder): boolean => {
        const isShipment = deliveryOrder.method === DeliveryMethod.SHIPMENT
        const hasPendingItems = deliveryOrder.items.some(item => item.deliveryStats?.pending > 0)
        const hasDispatchedItems = deliveryOrder.items.some(item => item.deliveryStats?.dispatched > 0)
        return (isShipment ? hasDispatchedItems : hasPendingItems) && !deliveryOrder.cancelled
    },

    isAnnulable: (deliveryOrder: DeliveryOrder): boolean => {
        const isReception = deliveryOrder.method === DeliveryMethod.RECEPTION
        const hasPendingItems = deliveryOrder.items.some(item => item.deliveryStats?.pending > 0)
        const hasDispatchedItems = deliveryOrder.items.some(item => item.deliveryStats?.dispatched > 0)
        return !isReception && hasPendingItems && !hasDispatchedItems && !deliveryOrder.cancelled
    },

    // An annulled order can still have delivered items (its pending ones were annulled), which can be returned
    isReturnable: (deliveryOrder: DeliveryOrder): boolean => {
        return deliveryOrder.items.some(item => item.deliveryStats?.delivered > 0)
    },

    getPendingQuantity: (operation: Operation, deliveryOrders: DeliveryOrder[], itemId: string): number => {
        const itemPending = operation.items.find(item => item.id === itemId)?.deliveryStats?.pending ?? 0
        const ordersPending = deliveryOrders
            .filter(deliveryOrder => !deliveryOrder.cancelled)
            .reduce((sum, deliveryOrder) => {
                const orderItem = deliveryOrder.items.find(orderItem => orderItem.itemId === itemId)
                return sum + (orderItem?.deliveryStats?.pending ?? 0)
            }, 0)
        return itemPending + ordersPending
    },

    isCancellable: (operation: Operation, deliveryOrders: DeliveryOrder[]): boolean => {
        const isDelivered = operation.deliveryStatus === DeliveryStatus.DELIVERED
        const isPending = operation.items.some(item => deliveryOrderHelpers.getPendingQuantity(operation, deliveryOrders, item.id) > 0)
        return !isDelivered && isPending
    },

    calculateDeliveryStatus: (deliveryOrder: DeliveryOrder): DeliveryStatus => {
        const items = deliveryOrder.items
        const hasDelivered = items.some(item => item.deliveryStats.delivered > 0)
        const hasDispatched = items.some(item => item.deliveryStats.dispatched > 0)
        const isFullyDelivered = items.every(item => (item.deliveryStats.pending + item.deliveryStats.dispatched) === 0)
        if (isFullyDelivered && hasDelivered) return DeliveryStatus.DELIVERED
        if (!hasDelivered && !hasDispatched) return DeliveryStatus.PENDING
        return DeliveryStatus.PARTIALLY_DELIVERED
    },

    // An order whose items were all cancelled/returned before any delivery has nothing left to deliver
    hasNothingToDeliver: (deliveryOrder: DeliveryOrder): boolean => {
        return deliveryOrder.items.every(item => item.deliveryStats.toBeDelivered <= 0)
    }
}
